import { service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import BaseAjaxService from 'ember-ajax/services/ajax';
import {
  getHostDomain,
  isPlatformLocalDomain,
} from 'frontend-utils/utils/domain-checks';
import Cookies from 'js-cookie';
import config from 'lumen/config/environment';

export function extendOptions(options) {
  if (isPlatformLocalDomain()) {
    options['crossDomain'] = true;
    options['xhrFields'] = { withCredentials: true };
  }

  if (!('contentType' in options)) {
    options['contentType'] = 'application/json';
  }

  if (macroCondition(isTesting())) {
    return options;
  }

  const csrftoken = Cookies.get('csrftoken');

  if (isWriteOperation(options['method']) && csrftoken) {
    options['headers'] = {
      'X-CSRFToken': csrftoken,
    };
  }

  return options;
}

function isWriteOperation(method) {
  return !/^(GET|HEAD|OPTIONS|TRACE)$/.test(method);
}

function transformErrorPayload(payload) {
  const newPayload = {
    errors: [
      {
        status: 400,
        detail: payload.error_message,
        code: payload.error_param,
      },
    ],
  };

  return newPayload;
}

export default class AjaxService extends BaseAjaxService {
  @service alerting;
  @service intl;

  host = getHostDomain({ useDevApi: config.useDevApi });
  namespace = 'api/v3';

  handleErrorUserUnauthorized() {
    const title = this.intl.primaryLocale
      ? this.intl.t('general.session_expired')
      : 'Session expired';

    const text = this.intl.primaryLocale
      ? this.intl.t('general.error.session_expired')
      : "Your session has expired. You'll be redirected to the login page.";

    void this.alerting
      .showErrorAlert({
        title,
        text,
        icon: '100x100_tangled',
        unsafe_outsideClickCanCloseAlert: false,
      })
      .then((hasConfirmed) => {
        if (!hasConfirmed) {
          return;
        }

        this.redirectToLogin();
      });
  }

  handleResponse(status, headers, payload, requestData) {
    if (status === 401) {
      this.handleErrorUserUnauthorized();
    }

    // TODO: remove when API is adjusted
    if (status === 400) {
      if (!payload.errors) {
        const newPayload = transformErrorPayload(payload);
        return super.handleResponse(status, headers, newPayload, requestData);
      }
    }

    return super.handleResponse(status, headers, payload, requestData);
  }

  redirectToLogin() {
    const nextUrlAfterLogin = encodeURIComponent(
      `${window.location.pathname}${window.location.search}`,
    );

    window.location.replace(`${this.host}/login/?next=${nextUrlAfterLogin}`);
  }

  request(url, options = {}) {
    return super.request(url, extendOptions(options));
  }
}
